import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Loader2, User, Lock, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function LoginPage() {
    const { login, signInWithGoogle } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({ username: '', password: '' });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await login(formData.username, formData.password);
            navigate('/dashboard');
        } catch (err) {
            setError(err.message || 'Failed to sign in');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError('');
        try {
            await signInWithGoogle();
            navigate('/dashboard');
        } catch (err) {
            setError('Failed to sign in with Google');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
                <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] bg-primary/5 rounded-full blur-3xl opacity-60"></div>
                <div className="absolute bottom-[10%] -left-[10%] w-[50%] h-[50%] bg-accent/10 rounded-full blur-3xl opacity-50"></div>
            </div>

            <div className="relative z-10 w-full max-w-md bg-white/80 dark:bg-card/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/50 dark:border-white/10">
                <div className="flex flex-col items-center mb-8">
                    <div className="h-20 w-20 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-primary/20 rotate-3 transform hover:rotate-6 transition-all duration-300">
                        <GraduationCap className="h-10 w-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-foreground tracking-tight font-heading">Welcome Back</h1>
                    <p className="text-muted-foreground mt-2 font-medium">Academic Portal Access</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm text-center font-medium animate-in fade-in slide-in-from-top-2">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-muted-foreground ml-1">Username</label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground/50" />
                            <input
                                type="text"
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                className="w-full bg-secondary/50 border border-border/50 rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                                placeholder="Enter your username"
                                required
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-muted-foreground ml-1">Password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground/50" />
                            <input
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                className="w-full bg-secondary/50 border border-border/50 rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                                placeholder="••••••••"
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={cn(
                            "w-full bg-primary text-primary-foreground py-3 px-6 rounded-xl font-semibold shadow-lg shadow-primary/25 mt-6",
                            "hover:bg-primary/90 hover:shadow-xl hover:-translate-y-0.5 transform",
                            "focus:ring-4 focus:ring-primary/20 focus:outline-none",
                            "transition-all duration-300 flex items-center justify-center gap-2",
                            "disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                        )}
                    >
                        {loading ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                            <>
                                <span>Sign In</span>
                                <ArrowRight className="h-5 w-5" />
                            </>
                        )}
                    </button>
                </form>

                <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border/50" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                    </div>
                </div>

                <button
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className={cn(
                        "w-full bg-white dark:bg-card border border-border/50 text-foreground py-3 px-6 rounded-xl font-semibold shadow-sm",
                        "hover:bg-secondary/50 transition-all duration-300 flex items-center justify-center gap-3",
                        "disabled:opacity-70 disabled:cursor-not-allowed"
                    )}
                >
                    <div className="bg-white p-0.5 rounded-full">
                        <img src="https://www.google.com/favicon.ico" alt="Google" className="h-5 w-5" />
                    </div>
                    <span className="text-sm">Google</span>
                </button>

                <div className="mt-8 pt-6 border-t border-border/50">
                    <p className="text-xs font-bold text-muted-foreground mb-3 text-center uppercase tracking-wider">Sample Credentials</p>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="p-2 bg-secondary/30 rounded-lg">
                            <div className="font-bold text-primary">HOD</div>
                            <div className="text-muted-foreground/70">hod</div>
                        </div>
                        <div className="p-2 bg-secondary/30 rounded-lg">
                            <div className="font-bold text-primary">Faculty</div>
                            <div className="text-muted-foreground/70">faculty</div>
                        </div>
                        <div className="p-2 bg-secondary/30 rounded-lg">
                            <div className="font-bold text-primary">Student</div>
                            <div className="text-muted-foreground/70">student</div>
                        </div>
                    </div>
                    <p className="text-[10px] text-center text-muted-foreground/50 mt-2">Password for all: password123</p>
                </div>
            </div>
        </div>
    );
}
